import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {SetAIGuideInteractionReactionType} from "./__graphql-types__/index.ts";
import type {SetAIGuideInteractionReactionVariables} from "./__graphql-types__/SetAIGuideInteractionReaction.ts";

export const AI_GUIDE_INTERACTION_REACTION_MUTATION: GqlOperation<
    SetAIGuideInteractionReactionType["response"],
    SetAIGuideInteractionReactionType["variables"]
> = gqlOp(gql`
    mutation SetAIGuideInteractionReaction(
        $threadId: ID!
        $interactionId: ID!
        $reaction: String
        $note: String
        $rewrittenResponse: String
        $rating: Int
        $sentiment: String
    ) {
        setAIGuideInteractionReaction(
            threadId: $threadId
            interactionId: $interactionId
            reaction: $reaction
            note: $note
            rewrittenResponse: $rewrittenResponse
            rating: $rating
            sentiment: $sentiment
        ) {
            error {
                code
            }
        }
    }
`);

/**
 * useSetAIGuideInteractionReaction is a hook that returns a mutation function
 * that is used to record feedback for an AIGuide response.
 */
export const useSetAIGuideInteractionReaction = (): ((
    variables: SetAIGuideInteractionReactionVariables,
) => Promise<void>) => {
    const [mutate] = useMutation(AI_GUIDE_INTERACTION_REACTION_MUTATION);

    const setAIGuideInteractionReaction = useCallback(
        async (variables: SetAIGuideInteractionReactionVariables) => {
            const result = await mutate(variables);
            // If we have an error status or we got a successful response but
            // it has an error in it, then we throw an error.
            // We should never see the loading status here since this waits
            // for the mutation to be complete, and we the aborted status is
            // something we can ignore for this case.
            const error =
                result.status === "error"
                    ? result.error
                    : result.status === "success" &&
                        result.data.setAIGuideInteractionReaction.error
                      ? result.data.setAIGuideInteractionReaction.error.code
                      : null;
            if (error) {
                throw new KAError(
                    "Failed to record AI Guide interaction reaction",
                    Errors.Internal,
                    {cause: error},
                );
            }
        },
        [mutate],
    );

    return setAIGuideInteractionReaction;
};
